/**
 * In-memory ring buffer for console logs, API errors, and global errors (bug report context).
 */

import { apiErrorToLogContext } from '../../utils/apiErrorHandler'
import { isBenignFrontendError } from '../../utils/benignFrontendErrors'
import { ApiError } from '../../services/api/core'

export type CaptureEntryKind = 'console' | 'api' | 'network' | 'error'

export interface CaptureEntry {
  ts: string
  kind: CaptureEntryKind
  level: string
  message: string
  context?: Record<string, unknown>
}

const MAX_ENTRIES = 200
const MAX_MESSAGE_LEN = 4000
// const MAX_CONTEXT_JSON = 8000

const SENSITIVE_QUERY_PARAMS = ['access_token', 'id_token', 'refresh_token', 'code']

const buffer: CaptureEntry[] = []

function sanitizeUrl(url: string): string {
  if (!url) return url
  try {
    const u = new URL(url)
    SENSITIVE_QUERY_PARAMS.forEach((p) => u.searchParams.delete(p))
    const s = u.toString()
    return s.length > 2000 ? s.slice(0, 2000) : s
  } catch {
    return url.length > 2000 ? url.slice(0, 2000) : url
  }
}

// function safeContext(value: unknown): Record<string, unknown> | undefined {
//   if (value == null) return undefined
//   try {
//     const seen = new WeakSet<object>()
//     const json = JSON.stringify(value, (_key, v) => {
//       if (typeof v === 'function') return '[Function]'
//       if (v instanceof Error) {
//         return { name: v.name, message: v.message, stack: v.stack?.slice(0, 2000) }
//       }
//       if (typeof v === 'object' && v !== null) {
//         if (seen.has(v)) return '[Circular]'
//         seen.add(v)
//       }
//       return v
//     })
//     if (!json || json === '{}') return undefined
//     if (json.length > MAX_CONTEXT_JSON) {
//       return { truncated: true, preview: json.slice(0, MAX_CONTEXT_JSON) }
//     }
//     return JSON.parse(json) as Record<string, unknown>
//   } catch {
//     return { raw: String(value).slice(0, 500) }
//   }
// }

function pushEntry(entry: CaptureEntry): void {
  try {
    buffer.push(entry)
    while (buffer.length > MAX_ENTRIES) {
      buffer.shift()
    }
  } catch {
    /* never break the app */
  }
}

export function captureBufferPush(
  kind: CaptureEntryKind,
  level: string,
  message: string,
  context?: Record<string, unknown>
): void {
  pushEntry({
    ts: new Date().toISOString(),
    kind,
    level,
    message: message.slice(0, MAX_MESSAGE_LEN),
    context,
  })
}

export function captureBufferPushApiError(endpoint: string, error: unknown): void {
  const ctx: Record<string, unknown> = { endpoint }
  if (error instanceof ApiError) {
    Object.assign(ctx, apiErrorToLogContext(error))
    ctx.message = error.message
  } else if (error instanceof Error) {
    ctx.message = error.message
    ctx.stack = error.stack?.slice(0, 2000)
  }
  captureBufferPush('api', 'error', error instanceof Error ? error.message : String(error), ctx)
}

export function captureBufferPushError(error: Error | unknown, component?: string, extra?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error))
  if (isBenignFrontendError(err.message)) return
  captureBufferPush('error', 'error', err.message, {
    component,
    stack: err.stack?.slice(0, 4000),
    ...extra,
  })
}

export function captureBufferSnapshot(): {
  capturedAt: string
  pageUrl: string
  userAgent: string
  entries: CaptureEntry[]
} {
  return {
    capturedAt: new Date().toISOString(),
    pageUrl: typeof window !== 'undefined' ? sanitizeUrl(window.location.href) : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    entries: [...buffer],
  }
}

export function captureBufferClear(): void {
  buffer.length = 0
}

export function snapshotToJsonFile(prefix = 'aqua-context'): File | null {
  try {
    const snap = captureBufferSnapshot()
    const json = JSON.stringify(snap, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const name = `${prefix}-${Date.now()}.json`
    return new File([blob], name, { type: 'application/json' })
  } catch {
    return null
  }
}
